import { FC, useState, useEffect } from 'react';
import { OrderResponse, OrderServiceTechnician } from '../../../api/orders';
import { Bay } from '../../../api/bays';
import { apiFetch } from '../../../utils/api';
import { UserProfile } from '../../../api/auth';

interface AssignmentEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    assignment: OrderServiceTechnician;
    order: OrderResponse;
    bays: Bay[];
    onSave: (assignmentId: string, data: { startTime?: string | null; endTime?: string | null; notes?: string | null; completed?: boolean; bayId?: string | null }) => Promise<void>;
}

const AssignmentEditModal: FC<AssignmentEditModalProps> = ({ isOpen, onClose, assignment, order, bays, onSave }) => {
    const [startTime, setStartTime] = useState<string>('');
    const [endTime, setEndTime] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [completed, setCompleted] = useState<boolean>(false);
    const [bayId, setBayId] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentTimeConflicts, setCurrentTimeConflicts] = useState<any[]>([]);
    const [fetchingAvailability, setFetchingAvailability] = useState(false);
    const [dayConflicts, setDayConflicts] = useState<any[]>([]);
    const [bayDayConflicts, setBayDayConflicts] = useState<any[]>([]);

    const isReadOnly = assignment?.completed;

    useEffect(() => {
        if (isOpen && assignment) {
            setStartTime(assignment.startTime || '');
            setEndTime(assignment.endTime || '');
            setNotes(assignment.notes || '');
            setCompleted(assignment.completed);
            setBayId(assignment.bayId || '');
            setError(null);
            setCurrentTimeConflicts([]);
            setDayConflicts([]);
            setBayDayConflicts([]);
        }
    }, [isOpen, assignment]);

    // Helper: build full-day ISO window for availability queries (standard 10AM-6PM)
    const buildDayWindow = (referenceIso?: string | null) => {
        // Priority: 1. Manual selection, 2. Order appointment date, 3. Today
        const refDate = referenceIso || order?.appointment?.scheduledAt || new Date().toISOString();
        const ref = new Date(refDate);
        const start = new Date(ref); start.setHours(10, 0, 0, 0);
        const end = new Date(ref); end.setHours(18, 0, 0, 0);
        return { dayStart: start.toISOString(), dayEnd: end.toISOString() };
    };

    // Fetch technician's full-day conflicts
    useEffect(() => {
        const techId = assignment?.technicianId;
        if (!isOpen || !techId) { setDayConflicts([]); return; }
        let isMounted = true;
        const { dayStart, dayEnd } = buildDayWindow(startTime);
        const url = `/orders/service-assignments/availability?technicianId=${encodeURIComponent(techId)}&startTime=${encodeURIComponent(dayStart)}&endTime=${encodeURIComponent(dayEnd)}`;
        apiFetch<any[]>(url)
            .then((data) => { if (isMounted) setDayConflicts(data || []); })
            .catch(() => { if (isMounted) setDayConflicts([]); });
        return () => { isMounted = false; };
    }, [isOpen, assignment?.technicianId, startTime]);

    // Fetch bay's full-day conflicts
    useEffect(() => {
        if (!isOpen || !bayId) { setBayDayConflicts([]); return; }
        let isMounted = true;
        const { dayStart, dayEnd } = buildDayWindow(startTime);
        const url = `/orders/service-assignments/availability?bayId=${encodeURIComponent(bayId)}&startTime=${encodeURIComponent(dayStart)}&endTime=${encodeURIComponent(dayEnd)}`;
        apiFetch<any[]>(url)
            .then((data) => { if (isMounted) setBayDayConflicts(data || []); })
            .catch(() => { if (isMounted) setBayDayConflicts([]); });
        return () => { isMounted = false; };
    }, [isOpen, bayId, startTime]);

    // Conflict detection
    useEffect(() => {
        if (!isOpen || !startTime || !endTime || !bayId) {
            setCurrentTimeConflicts([]);
            return;
        }

        let isMounted = true;
        setFetchingAvailability(true);
        const url = `/orders/service-assignments/availability?startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}&excludeAssignmentId=${encodeURIComponent(assignment.id || '')}`;

        apiFetch<any[]>(url)
            .then((data) => {
                if (isMounted) setCurrentTimeConflicts(data || []);
            })
            .catch((err) => {
                console.error('Failed to fetch availability', err);
                if (isMounted) setCurrentTimeConflicts([]);
            })
            .finally(() => {
                if (isMounted) setFetchingAvailability(false);
            });

        return () => { isMounted = false; };
    }, [isOpen, startTime, endTime, assignment.id, bayId]);

    const formatToHHMM = (dateStr: string | null): string => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const h = d.getHours().toString().padStart(2, '0');
        const m = d.getMinutes().toString().padStart(2, '0');
        return `${h}:${m}`;
    };

    const format12H = (date: Date | string | null): string => {
        if (!date) return '—';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const handleCompletedChange = (val: boolean) => {
        setCompleted(val);
        // User wants to keep EST End Time unchanged even when completed
    };

    const validateTimes = (start: string, end: string) => {
        if (start && end) {
            const startDate = new Date(start);
            const endDate = new Date(end);
            if (endDate <= startDate) {
                setError('EST End time must be after start time.');
                return false;
            }
            const startHHMM = formatToHHMM(start);
            const endHHMM = formatToHHMM(end);
            if (startHHMM < "10:00" || startHHMM > "18:00" || endHHMM < "10:00" || endHHMM > "18:00") {
                setError('Operating hours are 10:00 AM - 06:00 PM.');
                return false;
            }
        }
        setError(null);
        return true;
    };

    const handleStartTimeChange = (val: string) => {
        setStartTime(val);
        validateTimes(val, endTime);
    };

    const handleEndTimeChange = (val: string) => {
        setEndTime(val);
        validateTimes(startTime, val);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isReadOnly) return;
        if (!validateTimes(startTime, endTime)) return;

        // Strict conflict check
        const techConflict = currentTimeConflicts.find(c => String(c.technicianId) === String(assignment.technicianId));
        const bayConflict = currentTimeConflicts.find(c => String(c.bayId) === String(bayId));

        if (techConflict) {
            setError(`Technician busy: Already assigned to ${techConflict.orderNumber} ${format12H(techConflict.startTime)}-${format12H(techConflict.endTime)}.`);
            return;
        }
        if (bayConflict) {
            setError(`Bay occupied: Already in use for ${bayConflict.orderNumber} ${format12H(bayConflict.startTime)}-${format12H(bayConflict.endTime)}.`);
            return;
        }

        setLoading(true);
        try {
            await onSave(assignment.id, {
                startTime: startTime ? new Date(startTime).toISOString() : null,
                endTime: endTime ? new Date(endTime).toISOString() : null,
                notes: notes || null,
                completed,
                bayId: bayId || null,
            });
            onClose();
        } catch (error) {
            console.error('Failed to update assignment', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto p-4 bg-black/60 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all my-8">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                    <div className="flex flex-col">
                        <h3 className="font-bold text-slate-800 text-sm">Edit Assignment</h3>
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">SO: {order.orderNumber}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors p-1.5 hover:bg-slate-100 rounded-full">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Status Banner for Completed */}
                    {isReadOnly && (
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex gap-3 items-center">
                            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-xs font-bold text-emerald-700">This service is marked as completed.</span>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Technician</label>
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 truncate">
                                {assignment.technician?.firstName || assignment.technician?.username || 'Unknown'}
                            </div>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Bay</label>
                            <select
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none disabled:bg-slate-50 disabled:text-slate-400 h-[40px]"
                                value={bayId}
                                onChange={(e) => setBayId(e.target.value)}
                                disabled={isReadOnly}
                            >
                                <option value="">Select Bay</option>
                                {bays.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Start</label>
                            <input
                                type="time"
                                min="10:00"
                                max="18:00"
                                value={formatToHHMM(startTime)}
                                onChange={(e) => {
                                    const [h, m] = e.target.value.split(':');
                                    const d = startTime ? new Date(startTime) : new Date();
                                    d.setHours(parseInt(h), parseInt(m), 0, 0);

                                    // Keep duration
                                    const currentStart = startTime ? new Date(startTime) : null;
                                    const currentEnd = endTime ? new Date(endTime) : null;
                                    let durationMs = 30 * 60000;
                                    if (currentStart && currentEnd && currentEnd > currentStart) {
                                        durationMs = currentEnd.getTime() - currentStart.getTime();
                                    }
                                    const newEnd = new Date(d.getTime() + durationMs);
                                    setEndTime(newEnd.toISOString());
                                    handleStartTimeChange(d.toISOString());
                                }}
                                disabled={isReadOnly}
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none disabled:bg-slate-50 disabled:text-slate-400 h-[40px]"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Duration</label>
                            <select
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none disabled:bg-slate-50 disabled:text-slate-400 h-[40px]"
                                value={(() => {
                                    const start = startTime ? new Date(startTime) : null;
                                    const end = endTime ? new Date(endTime) : null;
                                    if (!start || !end || end <= start) return '30';
                                    return String(Math.round((end.getTime() - start.getTime()) / 60000));
                                })()}
                                onChange={(e) => {
                                    const mins = parseInt(e.target.value);
                                    const start = startTime ? new Date(startTime) : new Date();
                                    const end = new Date(start.getTime() + mins * 60000);
                                    setEndTime(end.toISOString());
                                }}
                                disabled={isReadOnly}
                            >
                                <option value="12">12 min</option>
                                <option value="15">15 min</option>
                                <option value="30">30 min</option>
                                <option value="45">45 min</option>
                                <option value="60">1 hour</option>
                                <option value="90">1.5 hours</option>
                                <option value="120">2 hours</option>
                                <option value="180">3 hours</option>
                                <option value="240">4 hours</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">End</label>
                            <input
                                type="time"
                                min="10:00"
                                max="18:00"
                                value={formatToHHMM(endTime)}
                                onChange={(e) => {
                                    const [h, m] = e.target.value.split(':');
                                    const d = endTime ? new Date(endTime) : (startTime ? new Date(startTime) : new Date());
                                    d.setHours(parseInt(h), parseInt(m), 0, 0);
                                    handleEndTimeChange(d.toISOString());
                                }}
                                disabled={isReadOnly}
                                className={`w-full px-3 py-1.5 border rounded-xl text-sm font-medium focus:ring-2 transition-all outline-none disabled:bg-slate-50 disabled:text-slate-400 h-[40px] ${error ? 'border-red-500 focus:ring-red-200' : 'border-slate-200 focus:ring-primary/20 focus:border-primary'}`}
                            />
                        </div>
                    </div>

                    {/* ── Day availability timelines ── */}
                    <div className="space-y-4 pt-2 border-t border-slate-100">
                        {/* Technician Timeline */}
                        {assignment.technician && (() => {
                            const DAY_START = 10 * 60;
                            const DAY_END = 18 * 60;
                            const DAY_MINS = DAY_END - DAY_START;
                            const pct = (mins: number) => `${Math.min(100, Math.max(0, ((mins - DAY_START) / DAY_MINS) * 100)).toFixed(2)}%`;
                            const toMins = (iso: string) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes(); };

                            const sameOrderTechSlots = (order.serviceTechnicians || [])
                                .filter(a => a.id !== assignment.id && String(a.technicianId) === String(assignment.technicianId) && a.startTime && a.endTime);

                            const otherOrderTechSlots = dayConflicts.filter(c => String(c.orderId) !== String(order.id));

                            const pendStart = startTime ? toMins(startTime) : null;
                            const pendEnd = endTime ? toMins(endTime) : null;
                            const pendConflicts = pendStart !== null && pendEnd !== null
                                ? otherOrderTechSlots.filter(c => {
                                    const s = toMins(c.startTime);
                                    const e = toMins(c.endTime);
                                    return s < pendEnd && e > pendStart;
                                })
                                : [];

                            const hourTicks = [10, 11, 12, 13, 14, 15, 16, 17, 18];

                            return (
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex justify-between items-center">
                                        <span>{assignment.technician?.firstName || 'Technician'}'s availability</span>
                                        {dayConflicts.length === 0 && !sameOrderTechSlots.length && <span className="text-emerald-500 lowercase">✓ fully free</span>}
                                    </p>
                                    <div className="relative h-6 w-full rounded-lg bg-emerald-50 border border-emerald-100 overflow-hidden">
                                        {hourTicks.map(h => <div key={h} className="absolute top-0 bottom-0 w-px bg-slate-200/60" style={{ left: pct(h * 60) }} />)}

                                        {/* External (red) */}
                                        {otherOrderTechSlots.map((c, i) => {
                                            const s = Math.max(toMins(c.startTime), DAY_START);
                                            const e = Math.min(toMins(c.endTime), DAY_END);
                                            if (e <= s) return null;
                                            return <div key={i} className="absolute top-0 bottom-0 bg-red-400/80" style={{ left: pct(s), width: `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%` }} />;
                                        })}

                                        {/* Same order (indigo) */}
                                        {sameOrderTechSlots.map((a, i) => {
                                            const s = Math.max(toMins(a.startTime!), DAY_START);
                                            const e = Math.min(toMins(a.endTime!), DAY_END);
                                            if (e <= s) return null;
                                            return <div key={i} className="absolute top-0 bottom-0" style={{ left: pct(s), width: `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%`, backgroundColor: '#6366f1' }} />;
                                        })}

                                        {/* Selection */}
                                        {pendStart !== null && pendEnd !== null && pendEnd > pendStart && (
                                            <div className={`absolute top-0 bottom-0 border-2 rounded ${pendConflicts.length > 0 ? 'bg-orange-300/50 border-orange-400' : 'bg-primary/30 border-primary'}`}
                                                style={{ left: pct(Math.max(pendStart, DAY_START)), width: `${((Math.min(pendEnd, DAY_END) - Math.max(pendStart, DAY_START)) / DAY_MINS * 100).toFixed(2)}%` }} />
                                        )}
                                    </div>
                                    <div className="relative h-3">
                                        {hourTicks.map(h => <span key={h} className="absolute text-[8px] text-slate-400 -translate-x-1/2" style={{ left: pct(h * 60) }}>{h < 12 ? `${h}A` : h === 12 ? '12P' : `${h - 12}P`}</span>)}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Bay Timeline */}
                        {bayId && (() => {
                            const DAY_START = 10 * 60;
                            const DAY_END = 18 * 60;
                            const DAY_MINS = DAY_END - DAY_START;
                            const pct = (mins: number) => `${Math.min(100, Math.max(0, ((mins - DAY_START) / DAY_MINS) * 100)).toFixed(2)}%`;
                            const toMins = (iso: string) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes(); };

                            const sameOrderBaySlots = (order.serviceTechnicians || [])
                                .filter(a => a.id !== assignment.id && String(a.bayId) === String(bayId) && a.startTime && a.endTime);

                            const otherOrderBaySlots = bayDayConflicts.filter(c => String(c.orderId) !== String(order.id));

                            const pendStart = startTime ? toMins(startTime) : null;
                            const pendEnd = endTime ? toMins(endTime) : null;
                            const pendConflicts = pendStart !== null && pendEnd !== null
                                ? otherOrderBaySlots.filter(c => {
                                    const s = toMins(c.startTime);
                                    const e = toMins(c.endTime);
                                    return s < pendEnd && e > pendStart;
                                })
                                : [];

                            const bayName = bays.find(b => String(b.id) === String(bayId))?.name || 'Bay';

                            return (
                                <div className="space-y-1.5">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex justify-between items-center">
                                        <span>{bayName} occupancy</span>
                                        {bayDayConflicts.length === 0 && !sameOrderBaySlots.length && <span className="text-emerald-500 lowercase">✓ bay free</span>}
                                    </p>
                                    <div className="relative h-6 w-full rounded-lg bg-amber-50 border border-amber-100 overflow-hidden">
                                        {[10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => <div key={h} className="absolute top-0 bottom-0 w-px bg-slate-200/60" style={{ left: pct(h * 60) }} />)}

                                        {/* External (amber) */}
                                        {otherOrderBaySlots.map((c, i) => {
                                            const s = Math.max(toMins(c.startTime), DAY_START);
                                            const e = Math.min(toMins(c.endTime), DAY_END);
                                            if (e <= s) return null;
                                            return <div key={i} className="absolute top-0 bottom-0 bg-amber-500/80" style={{ left: pct(s), width: `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%` }} />;
                                        })}

                                        {/* Same order (indigo) */}
                                        {sameOrderBaySlots.map((a, i) => {
                                            const s = Math.max(toMins(a.startTime!), DAY_START);
                                            const e = Math.min(toMins(a.endTime!), DAY_END);
                                            if (e <= s) return null;
                                            return <div key={i} className="absolute top-0 bottom-0" style={{ left: pct(s), width: `${(((e - s) / DAY_MINS) * 100).toFixed(2)}%`, backgroundColor: '#6366f1' }} />;
                                        })}

                                        {/* Selection */}
                                        {pendStart !== null && pendEnd !== null && pendEnd > pendStart && (
                                            <div className={`absolute top-0 bottom-0 border-2 rounded ${pendConflicts.length > 0 ? 'bg-red-300/50 border-red-400' : 'bg-primary/30 border-primary'}`}
                                                style={{ left: pct(Math.max(pendStart, DAY_START)), width: `${((Math.min(pendEnd, DAY_END) - Math.max(pendStart, DAY_START)) / DAY_MINS * 100).toFixed(2)}%` }} />
                                        )}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 pt-1">
                                        <span className="flex items-center gap-1 text-[8px] text-slate-400 font-bold uppercase"><span className="w-2 h-2 rounded-full bg-red-400" /> Conflict</span>
                                        <span className="flex items-center gap-1 text-[8px] text-slate-400 font-bold uppercase"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#6366f1' }} /> This Order</span>
                                        <span className="flex items-center gap-1 text-[8px] text-slate-400 font-bold uppercase"><span className="w-2 h-2 rounded-full border-2 border-primary bg-primary/20" /> Selection</span>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* ── Smart Free-Slot Suggestions ── */}
                        {(assignment.technician || bayId) && (() => {
                            const DAY_START_MIN = 10 * 60;
                            const DAY_END_MIN = 18 * 60;
                            const toMins = (iso: string) => { const d = new Date(iso); return d.getHours() * 60 + d.getMinutes(); };

                            // Combine all relevant busy slots
                            const techSlots = [
                                ...dayConflicts.map(c => ({ s: toMins(c.startTime), e: toMins(c.endTime) })),
                                ...(order.serviceTechnicians || [])
                                    .filter(a => a.id !== assignment.id && String(a.technicianId) === String(assignment.technicianId) && a.startTime && a.endTime)
                                    .map(a => ({ s: toMins(a.startTime!), e: toMins(a.endTime!) }))
                            ];
                            const baySlots = [
                                ...bayDayConflicts.map(c => ({ s: toMins(c.startTime), e: toMins(c.endTime) })),
                                ...(order.serviceTechnicians || [])
                                    .filter(a => a.id !== assignment.id && String(a.bayId) === String(bayId) && a.startTime && a.endTime)
                                    .map(a => ({ s: toMins(a.startTime!), e: toMins(a.endTime!) }))
                            ];

                            const allBusy = [...techSlots, ...baySlots].sort((a, b) => a.s - b.s);
                            const merged: { s: number, e: number }[] = [];
                            for (const b of allBusy) {
                                if (!merged.length || b.s > merged[merged.length - 1].e) merged.push({ ...b });
                                else merged[merged.length - 1].e = Math.max(merged[merged.length - 1].e, b.e);
                            }

                            const freeGaps: { s: number, e: number }[] = [];
                            let last = DAY_START_MIN;
                            for (const m of merged) {
                                if (m.s > last + 10) freeGaps.push({ s: last, e: m.s });
                                last = Math.max(last, m.e);
                            }
                            if (last < DAY_END_MIN - 10) freeGaps.push({ s: last, e: DAY_END_MIN });

                            if (freeGaps.length === 0) return null;

                            const format12HLocal = (mins: number) => {
                                const h = Math.floor(mins / 60);
                                const m = mins % 60;
                                const ampm = h >= 12 ? 'PM' : 'AM';
                                const hh = h % 12 || 12;
                                return `${hh}:${m.toString().padStart(2, '0')} ${ampm}`;
                            };

                            const applySuggestion = (s: number) => {
                                const ref = startTime ? new Date(startTime) : (order?.appointment?.scheduledAt ? new Date(order.appointment.scheduledAt) : new Date());
                                const d = new Date(ref);
                                d.setHours(Math.floor(s / 60), s % 60, 0, 0);

                                const currentStart = startTime ? new Date(startTime) : null;
                                const currentEnd = endTime ? new Date(endTime) : null;
                                let dur = 30 * 60000;
                                if (currentStart && currentEnd && currentEnd > currentStart) dur = currentEnd.getTime() - currentStart.getTime();

                                const newEnd = new Date(d.getTime() + dur);
                                setEndTime(newEnd.toISOString());
                                setStartTime(d.toISOString());
                                setError(null);
                            };

                            return (
                                <div className="pt-2 animate-in fade-in slide-in-from-top-1 duration-300">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
                                        <svg className="w-3 h-3 text-emerald-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                                        Suggested Free Slots
                                    </p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {freeGaps.map((g, i) => (
                                            <button
                                                key={i}
                                                type="button"
                                                onClick={() => applySuggestion(g.s)}
                                                className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-[10px] font-bold text-emerald-700 transition-all hover:scale-105 active:scale-95"
                                            >
                                                {format12HLocal(g.s)} - {format12HLocal(g.e)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                    {error && <p className="text-[10px] text-red-500 font-bold uppercase tracking-tight ml-1">{error}</p>}

                    <div className="space-y-1">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Work Notes</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={2}
                            disabled={isReadOnly}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none resize-none disabled:bg-slate-50 disabled:text-slate-400"
                            placeholder="Add details about the work performed..."
                        />
                    </div>

                    {!isReadOnly && (
                        <div className="flex items-center gap-3 bg-slate-50/50 p-2.5 rounded-xl border border-slate-100 group cursor-pointer" onClick={() => handleCompletedChange(!completed)}>
                            <div className={`h-4.5 w-4.5 rounded border-2 flex items-center justify-center transition-all ${completed ? 'bg-primary border-primary' : 'bg-white border-slate-200'}`}>
                                {completed && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                            </div>
                            <label className="text-xs font-bold text-slate-700 select-none cursor-pointer">Mark as Completed</label>
                        </div>
                    )}

                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 text-xs font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all active:scale-95"
                        >
                            {isReadOnly ? 'Close' : 'Cancel'}
                        </button>
                        {!isReadOnly && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 text-xs font-bold text-white bg-primary hover:bg-primary-dark rounded-xl shadow-lg shadow-primary/20 transition-all active:scale-95 disabled:opacity-50"
                            >
                                {loading ? 'Saving...' : 'Save Changes'}
                            </button>
                        )}
                    </div>
                </form>
            </div >
        </div >
    );
};

export default AssignmentEditModal;
